import { Link } from '@tanstack/react-router'
import { mdiChevronRight } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { useCampaignUnits, type Campaign } from '../../hooks/useCampaigns'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import { AppIcon } from '../ui/icon'
import { Skeleton } from '../ui/skeleton'
import { UNIT_STATUS } from './campaign-status'
import { StatusPill } from './StatusPill'

/** The units of a campaign the caller sees, each opening its form. */
export function CampaignUnitsTab({ org, campaign }: { org: string; campaign: Campaign }) {
    const { t } = useTranslation('campaigns')
    const { data: units, isLoading } = useCampaignUnits(org, campaign.uid)
    if (campaign.status === 'draft') {
        return <EmptyState icon={SECTION_ICONS.campaigns} title={t('units_draft_title')} description={t('units_draft_description')} data-testid="campaign-units-draft" />
    }
    if (isLoading) return <Skeleton className="h-32 w-full" />
    if (!units || units.length === 0) {
        return <EmptyState icon={SECTION_ICONS.campaigns} title={t('units_empty')} data-testid="campaign-units-empty" />
    }
    return (
        <ul className="divide-y rounded-lg border" data-testid="campaign-units">
            {units.map((u) => (
                <li key={u.uid}>
                    <Link
                        to="/$org/campaigns/$campaignUid/units/$unitUid"
                        params={{ org, campaignUid: campaign.uid, unitUid: u.uid }}
                        className="flex items-center gap-3 p-3 hover:bg-muted/50"
                        data-testid={`campaign-unit-${u.asset.name}`}
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="truncate font-medium">{u.asset.name}</span>
                                <StatusPill map={UNIT_STATUS} family="unit_status" value={u.status} />
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {[u.site?.name, u.building && u.building.uid !== u.asset.uid ? u.building.name : undefined]
                                    .filter(Boolean)
                                    .join(' · ')}
                                {' '}
                                {t('answered_count', { answered: u.answered, total: u.entries })}
                                {u.required_open > 0 && ` · ${t('required_open', { count: u.required_open })}`}
                            </p>
                        </div>
                        <AppIcon path={mdiChevronRight} size="sm" className="text-muted-foreground" />
                    </Link>
                </li>
            ))}
        </ul>
    )
}
